import React, { ReactNode, useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'

// evertel
import { Badge, Card, CardBody, CardHeader, Text, useRovingTabIndex } from '@evertel/web/ui'
import { APIDataDocumentSchema } from '@evertel/types'

// focus plumbing each card needs to take part in the grid's single Tab stop
interface TemplateGridItemProps {
    tabIndex: number
    onFocus: () => void
}

interface TemplateGridProps {
    // DOM id; the category tabs point aria-controls at it
    id: string
    // id of the selected category tab
    labelledBy: string
    title: string
    schemas: APIDataDocumentSchema[]
    renderCard: (schema: APIDataDocumentSchema, itemProps: TemplateGridItemProps) => ReactNode
    emptyMessage?: string
}

const TemplateGrid: React.FC<TemplateGridProps> = ({
    id,
    labelledBy,
    title,
    schemas,
    renderCard,
    emptyMessage = 'No templates in this category yet.'
}) => {

    // the card that owns the grid's Tab stop: the last one focused while it's still listed,
    // otherwise the first. Tracking the id (not the index) means a category switch or a
    // template being enabled/disabled falls back sensibly without an effect
    const [activeId, setActiveId] = useState<number | undefined>()
    const activeIndex = Math.max(0, schemas.findIndex(s => s.id === activeId))

    // Left/Right walk the grid in reading order and wrap across rows; Up/Down move by a
    // whole row (column count comes from the grid's computed tracks); Home/End jump
    const { containerRef, onKeyDown } = useRovingTabIndex({
        itemSelector: '.document-template-card',
        orientation: 'grid',
        wrap: true
    })

    const hintId = `${id}-keyboard-hint`

    return (
        <Card
            id={id}
            role="tabpanel"
            aria-labelledby={labelledBy}
            className="w-100 template-browse-panel">
            <CardHeader>
                <Text
                    tag="h5"
                    className="mb-0">
                    {title}
                </Text>
                {(!!schemas.length) &&
                    <Badge
                        color="primary"
                        className="ms-2">
                        {schemas.length}
                    </Badge>
                }
            </CardHeader>
            <CardBody>
                {(schemas.length) ? (
                    <PerfectScrollbar
                        options={{ suppressScrollX: true }}
                        style={{ height: '100%' }}>
                        {/* arrow keys on buttons aren't a convention screen reader users will
                            expect, so say so */}
                        <span
                            id={hintId}
                            className="visually-hidden">
                            Use the arrow keys to move between templates.
                        </span>
                        <div
                            ref={containerRef}
                            role="group"
                            aria-label={`${title} templates`}
                            aria-describedby={hintId}
                            className="document-template-card-grid"
                            onKeyDown={onKeyDown}>
                            {schemas.map((schema, index) => renderCard(schema, {
                                tabIndex: index === activeIndex ? 0 : -1,
                                onFocus: () => setActiveId(schema.id as number)
                            }))}
                        </div>
                    </PerfectScrollbar>
                ) : (
                    <Text
                        tag="p"
                        color="muted"
                        className="text-center my-5">
                        {emptyMessage}
                    </Text>
                )}
            </CardBody>
        </Card>
    )
}

export { TemplateGrid }
export type { TemplateGridItemProps }
